/**
 * Header — barra superior verde (LDS) com logo Localiza, título, menu do
 * usuário e indicador persistente do Managed_Environment ativo.
 *
 * Requisitos: 14.2 (header verde com logo + ambiente), 3.7 (indicador persistente).
 */

import { useState, useRef } from 'react';
import localizaCoLogo from './assets/localiza-co-header.svg';
import irondomeLogo from './assets/irondome-logo-final-removebg-preview.png';
import styles from './Header.module.scss';

export interface HeaderProps {
  /** Managed_Environment ativo (np|prd). */
  managedEnvironment: string;
  /** Ambientes que o deployment pode operar. */
  authorizedEnvironments: string[];
  /** Callback ao trocar de ambiente. */
  onEnvironmentChange?: (env: string) => void;
  /** Display name do usuário autenticado. */
  userName?: string;
  /** Email/UPN do usuário. */
  userEmail?: string;
  /** Callback de logout. */
  onLogout?: () => void;
}

function envLabel(env: string): string {
  if (env === 'np') return 'Não produtivo';
  if (env === 'prd') return 'Produtivo';
  return env.toUpperCase();
}

export function Header({ managedEnvironment, authorizedEnvironments, onEnvironmentChange, userName, userEmail, onLogout }: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Iniciais do nome para o avatar
  const initials = userName
    ? userName.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : '';

  return (
    <header className={styles.header}>
      <div className={styles.brand}>
        <img src={localizaCoLogo} alt="Localiza&Co" height="36" />
        <span className={styles.separator} aria-hidden="true" />
        <img src={irondomeLogo} alt="Iron Dome" height="80" className={styles.appLogo} />
        <span className={styles.title}>
          <span className={styles.titleMain}>Iron Dome</span>
          <span className={styles.titleSub}>Security Guardrails</span>
        </span>
      </div>

      <div className={styles.rightSection}>
        {/* User menu trigger */}
        {userName && (
          <div className={styles.userMenu} ref={menuRef}>
            <button
              className={styles.userButton}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu do usuário"
              aria-expanded={menuOpen}
              data-testid="user-menu-button"
            >
              <span className={styles.avatar}>{initials}</span>
              <span className={styles.userInfo}>
                <span className={styles.userName}>{userName}</span>
                <span className={styles.userEnv}>{envLabel(managedEnvironment)}</span>
              </span>
            </button>

            {/* Dropdown panel */}
            {menuOpen && (
              <div className={styles.dropdown} data-testid="user-menu-dropdown">
                <div className={styles.dropdownHeader}>
                  <span className={styles.dropdownName}>{userName}</span>
                  {userEmail && <span className={styles.dropdownEmail}>{userEmail}</span>}
                </div>
                <hr className={styles.dropdownDivider} />
                {authorizedEnvironments.length > 1 && (
                  <>
                    <button
                      className={styles.dropdownItem}
                      onClick={() => {
                        const next = authorizedEnvironments.find(e => e !== managedEnvironment);
                        if (next) { onEnvironmentChange?.(next); }
                        setMenuOpen(false);
                      }}
                      data-action="switch-env"
                    >
                      <span className={styles.dropdownItemIcon}>⇄</span>
                      Trocar para {envLabel(authorizedEnvironments.find(e => e !== managedEnvironment) ?? '')}
                    </button>
                    <hr className={styles.dropdownDivider} />
                  </>
                )}
                <button
                  className={styles.dropdownItem}
                  onClick={() => {
                    setMenuOpen(false);
                    onLogout?.();
                  }}
                  aria-label="Log Out"
                  data-action="logout"
                >
                  <span className={styles.dropdownItemIcon}>↪</span>
                  Log Out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
